import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Calendar, Clock, DollarSign, MapPin, Building2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';

// Format date - supports user preference
const formatDate = (date, dateFormat = 'DD/MM/YYYY') => {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });
  const year = d.getFullYear();

  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${monthShort} ${day}, ${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${day} ${monthShort} ${year}`;
  }
};

// Format time - supports user preference
const formatTime = (date, timeFormat = '12h') => {
  if (!date) return '';
  const d = new Date(date);
  if (timeFormat === '24h') {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Format datetime - supports user preference
const formatDateTime = (date, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  if (!date) return '';
  return `${formatDate(date, dateFormat)} ${formatTime(date, timeFormat)}`;
};

export function EarningsListItem({ earning }) {
  const { preferences } = useUserPreferences();
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'rejected':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-surface-light text-text-secondary border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-surface rounded-xl border border-border hover:border-primary transition-all duration-300 overflow-hidden">
      {/* Main Content */}
      <div
        className="p-6 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-text-primary">
                {earning?.jobName}
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(earning?.status)}`}>
                {getStatusIcon(earning?.status)}
                {earning?.status?.charAt(0)?.toUpperCase() + earning?.status?.slice(1)}
              </span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-text-secondary" />
                <span className="text-text-secondary">{earning?.companyName}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-text-secondary" />
                <span className="text-text-secondary">
                  {formatDate(earning.clockInTime, preferences.date_format)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-text-secondary" />
                <span className="text-text-secondary">{earning?.totalHours}h</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-green-500" />
                <span className="text-green-500 font-semibold">
                  ${earning?.amountEarned?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          
          <button className="ml-4 p-2 hover:bg-surface-light rounded-lg transition-colors">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-text-secondary" />
            ) : (
              <ChevronDown className="w-5 h-5 text-text-secondary" />
            )}
          </button>
        </div>
      </div>
      {/* Expanded Details */}
      {expanded && (
        <div className="px-6 pb-6 border-t border-border pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Job Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary mb-3">Job Details</h4>

              {earning?.locationAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-text-secondary mt-0.5" />
                  <div>
                    <p className="text-xs text-text-secondary">Location</p>
                    <p className="text-sm text-text-primary">{earning?.locationAddress}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <DollarSign className="w-4 h-4 text-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-text-secondary">Payment Type</p>
                  <p className="text-sm text-text-primary capitalize">{earning?.paymentType}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <DollarSign className="w-4 h-4 text-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-text-secondary">Base Rate</p>
                  <p className="text-sm text-text-primary">
                    ${earning?.paymentAmount?.toFixed(2)}{earning?.paymentType === 'hourly' ? '/hr' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Time Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary mb-3">Time Breakdown</h4>

              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-text-secondary">Clock In</p>
                  <p className="text-sm text-text-primary">
                    {formatTime(earning.clockInTime, preferences.time_format)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-text-secondary">Clock Out</p>
                  <p className="text-sm text-text-primary">
                    {formatTime(earning.clockOutTime, preferences.time_format)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-text-secondary mt-0.5" />
                <div>
                  <p className="text-xs text-text-secondary">Total Hours</p>
                  <p className="text-sm text-text-primary font-semibold">
                    {earning?.totalHours} hours
                  </p>
                </div>
              </div>

              {earning?.approvedAt && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-text-secondary">Approved On</p>
                    <p className="text-sm text-green-500">
                      {formatDateTime(earning.approvedAt, preferences.date_format, preferences.time_format)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Earnings Breakdown */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="bg-surface-light p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Base Earnings</span>
                <span className="text-text-primary font-semibold">
                  ${earning?.amountEarned?.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-border">
                <span className="text-lg font-semibold text-text-primary">Total Earned</span>
                <span className="text-2xl font-bold text-green-500">
                  ${earning?.amountEarned?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}